import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { publicShotListAPI } from '../utils/api';
import { Camera, Plus, CheckCircle2, Sparkles, Heart, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { key: 'must_have', label: 'Must Have' },
  { key: 'getting_ready', label: 'Getting Ready' },
  { key: 'ceremony', label: 'Ceremony' },
  { key: 'family', label: 'Family' },
  { key: 'couple', label: 'Couple' },
  { key: 'details', label: 'Details' },
  { key: 'reception', label: 'Reception' },
  { key: 'candid', label: 'Candid' },
  { key: 'other', label: 'Other' },
];

const PublicShotList = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingImagePath, setPendingImagePath] = useState(null);
  const [name, setName] = useState(() => localStorage.getItem('shotlist_name') || '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('must_have');

  const fetchList = useCallback(async () => {
    try {
      const r = await publicShotListAPI.get(token);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load this shot list. The link may have expired.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await publicShotListAPI.uploadReference(token, formData);
      setPendingImagePath(r.data.path);
      toast.success('Image attached');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (!title.trim()) { toast.error('Please enter a shot title'); return; }
    setSubmitting(true);
    try {
      await publicShotListAPI.submit(token, {
        title: title.trim(),
        description: description.trim(),
        category,
        submitted_by_name: name.trim(),
        reference_image_path: pendingImagePath,
      });
      localStorage.setItem('shotlist_name', name.trim());
      toast.success('Shot added! 💛');
      setTitle('');
      setDescription('');
      setPendingImagePath(null);
      setCategory('must_have');
      setShowForm(false);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = useMemo(() => {
    if (!data?.items) return {};
    return data.items.reduce((acc, item) => {
      const k = item.category || 'other';
      (acc[k] = acc[k] || []).push(item);
      return acc;
    }, {});
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading shot list…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="card p-8 text-center max-w-md w-full">
          <X size={48} className="text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-medium mb-2">Link not available</h2>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const couple = data?.project?.bride_name && data?.project?.groom_name
    ? `${data.project.bride_name} & ${data.project.groom_name}`
    : '';

  return (
    <div className="min-h-screen bg-background" data-testid="public-shotlist-page">
      {/* Header */}
      <header className="border-b border-border bg-background sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-5">
          <div className="flex items-center gap-2 mb-1">
            <Camera size={20} className="text-gold" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ami Video · Shot List</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif">{couple}</h1>
          {data?.event && (
            <p className="text-sm text-muted-foreground mt-1">
              {data.event.event_name} · {data.event.event_date}{data.event.start_time ? ` · ${data.event.start_time}` : ''}
            </p>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Intro */}
        <div className="card p-4 sm:p-5 mb-5 border-l-4 border-gold">
          <p className="text-sm flex items-start gap-2">
            <Heart size={16} className="text-gold mt-0.5 flex-shrink-0" />
            <span>
              Hi! 👋 This is your shot list. Add any specific shots you want — family combinations, must-have moments, Pinterest references, anything. We&apos;ll review and bring it to your event.
            </span>
          </p>
        </div>

        {/* Add button */}
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="w-full mb-5 py-3 bg-primary text-primary-foreground inline-flex items-center justify-center gap-2 hover:bg-black/90"
            data-testid="public-shotlist-add-btn"
          >
            <Plus size={18} /> Add a shot
          </button>
        )}

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="card p-4 sm:p-5 mb-5 space-y-3" data-testid="public-shotlist-form">
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Your name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Heena (bride)"
                className="w-full px-3 py-2 border border-input bg-background"
                data-testid="public-shotlist-name"
                required
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Shot</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., All four grandparents together"
                className="w-full px-3 py-2 border border-input bg-background"
                data-testid="public-shotlist-title"
                required
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Notes (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Anything we should know — locations, special meaning, etc."
                className="w-full px-3 py-2 border border-input bg-background"
                data-testid="public-shotlist-desc"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-input bg-background"
                data-testid="public-shotlist-category"
              >
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Reference photo (optional)</label>
              {pendingImagePath ? (
                <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 text-sm text-green-800 dark:text-green-300">
                  <CheckCircle2 size={14} /> Image attached
                  <button type="button" onClick={() => setPendingImagePath(null)} className="ml-auto text-green-700 dark:text-green-400 hover:text-red-600 dark:text-red-400">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label className="block cursor-pointer p-3 border border-dashed border-border hover:border-gold text-sm text-muted-foreground text-center">
                  <ImageIcon size={16} className="inline mr-1" />
                  {uploading ? 'Uploading…' : 'Tap to attach a reference photo (e.g., Pinterest screenshot)'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                    data-testid="public-shotlist-upload"
                  />
                </label>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setTitle(''); setDescription(''); setPendingImagePath(null); }}
                className="flex-1 px-3 py-2 text-sm border border-border hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-3 py-2 text-sm bg-primary text-primary-foreground hover:bg-black/90 disabled:opacity-50"
                data-testid="public-shotlist-submit"
              >
                {submitting ? 'Adding…' : 'Add Shot'}
              </button>
            </div>
          </form>
        )}

        {/* Items grouped by category */}
        {CATEGORIES.map(({ key, label }) => {
          const items = grouped[key] || [];
          if (items.length === 0) return null;
          return (
            <section key={key} className="mb-6" data-testid={`public-shotlist-section-${key}`}>
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{label} · {items.length}</h3>
              <div className="space-y-2">
                {items.map(item => (
                  <div key={item.item_id} className="card p-3 flex items-start gap-3">
                    {item.reference_image_path && <PublicThumb path={item.reference_image_path} token={token} />}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${item.captured ? 'line-through text-muted-foreground' : ''}`}>
                        {item.title}
                      </p>
                      {item.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                        {item.source === 'client' ? (
                          <span className="inline-flex items-center gap-0.5 text-pink-600 dark:text-pink-400">
                            <Sparkles size={10} /> {item.submitted_by_name || 'Couple'}
                          </span>
                        ) : (
                          <span className="text-gold">From your photographer</span>
                        )}
                        {item.captured && <span className="text-green-700 dark:text-green-400">✓ Captured</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        {(!data?.items || data.items.length === 0) && !showForm && (
          <div className="card p-8 text-center text-muted-foreground text-sm">
            Nothing here yet. Tap “Add a shot” above to start your list.
          </div>
        )}
      </main>
    </div>
  );
};

const PublicThumb = ({ path, token }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let active = true;
    const filename = path.replace(/^.*\/shots\//, '');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/shots/${filename}?token=${token}`)
      .then(r => r.ok ? r.blob() : null)
      .then(b => { if (active && b) setBlobUrl(URL.createObjectURL(b)); })
      .catch(() => {});
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, token]);
  return (
    <div className="flex-shrink-0 w-14 h-14 bg-muted border border-border overflow-hidden">
      {blobUrl ? (
        <img src={blobUrl} alt="reference" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
          <ImageIcon size={14} />
        </div>
      )}
    </div>
  );
};

export default PublicShotList;
